// src/index.ts
import { create as create2 } from "zustand";

// src/streamingStateStore.ts
import { create } from "zustand";
var useStreamingStateStore = create((set, get) => ({
  // Map of messageId -> { streaming: boolean, hasContent: boolean, generation_id: string|null, latest_seq: number }
  activeStreams: {},
  // Persistent map of messageId -> { generation_id, latest_seq } for in-progress generations.
  // Survives navigation (detachStreaming) so the chat mount effect can reattach.
  // Cleared when a terminal event arrives (completeStreaming / failStreaming).
  pendingGenerations: {},
  connectionRequirements: {},
  // Set of messageIds that were explicitly stopped by the user
  // Prevents database listener from refetching the complete backend answer
  userStoppedIds: /* @__PURE__ */ new Set(),
  // Stop was acknowledged but has not reached a durable terminal state yet.
  stoppingMessageIds: /* @__PURE__ */ new Set(),
  /**
   * Mark a message as actively streaming
   */
  startStreaming: (messageId) => {
    set((state) => {
      const newUserStopped = new Set(state.userStoppedIds);
      newUserStopped.delete(messageId);
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      stoppingMessageIds.delete(messageId);
      const nextConnectionRequirements = { ...state.connectionRequirements };
      delete nextConnectionRequirements[messageId];
      return {
        activeStreams: {
          ...state.activeStreams,
          [messageId]: { streaming: true, hasContent: false, generation_id: null, latest_seq: 0 }
        },
        connectionRequirements: nextConnectionRequirements,
        userStoppedIds: newUserStopped,
        stoppingMessageIds
      };
    });
  },
  /**
   * Resume an approved generation without discarding its pre-approval Activity.
   */
  resumeStreaming: (messageId, persistedActivityItems = []) => {
    set((state) => {
      const newUserStopped = new Set(state.userStoppedIds);
      newUserStopped.delete(messageId);
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      stoppingMessageIds.delete(messageId);
      const nextConnectionRequirements = { ...state.connectionRequirements };
      delete nextConnectionRequirements[messageId];
      const mergedActivity = /* @__PURE__ */ new Map();
      for (const item of state.activeStreams[messageId]?.activityItems || []) {
        mergedActivity.set(item.seq, item);
      }
      for (const item of persistedActivityItems) {
        mergedActivity.set(item.seq, item);
      }
      return {
        activeStreams: {
          ...state.activeStreams,
          [messageId]: {
            streaming: true,
            hasContent: false,
            generation_id: null,
            latest_seq: 0,
            activityItems: [...mergedActivity.values()].sort((a, b) => a.seq - b.seq)
          }
        },
        connectionRequirements: nextConnectionRequirements,
        userStoppedIds: newUserStopped,
        stoppingMessageIds
      };
    });
  },
  setConnectionRequirement: (messageId, requirement) => {
    set((state) => ({
      connectionRequirements: {
        ...state.connectionRequirements,
        [messageId]: requirement
      }
    }));
  },
  /**
   * Mark whether the stream currently holds any answer text. Passing false is
   * how a tool round's discarded narration is undone.
   */
  setStreamHasContent: (messageId, hasContent = true) => {
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          hasContent
        }
      }
    }));
  },
  /**
   * Update the live status text for a streaming message (from backend STATUS events)
   */
  setStatusText: (messageId, text) => {
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          statusText: text
        }
      }
    }));
  },
  /**
   * Get the current status text for a streaming message
   */
  getStatusText: (messageId) => {
    return get().activeStreams[messageId]?.statusText || null;
  },
  /**
   * Add a tool call entry (from TOOL_START event). Status starts as 'running'.
   */
  addToolCall: (messageId, { call_id, name, display_name, hint, code, args, agent_id, agent_name, cancellation_capability, cancellation_limitation }) => {
    set((state) => {
      const toolCalls = state.activeStreams[messageId]?.toolCalls || [];
      if (toolCalls.some((toolCall) => toolCall.callId === call_id)) return state;
      return {
        activeStreams: {
          ...state.activeStreams,
          [messageId]: {
            ...state.activeStreams[messageId],
            toolCalls: [
              ...toolCalls,
              {
                callId: call_id,
                name,
                displayName: display_name,
                hint: hint || null,
                // code: set for run_python_code; args: set for MCP/other tools
                code: code || null,
                args: args || null,
                stdout: null,
                stderr: null,
                size_hint: null,
                status: "running",
                startedAt: Date.now(),
                error: null,
                agentId: agent_id || null,
                agentName: agent_name || null,
                cancellationCapability: cancellation_capability || "non_interruptible",
                cancellationLimitation: cancellation_limitation || null
              }
            ]
          }
        }
      };
    });
  },
  /**
   * Bulk-seed activityItems from the persisted answer on reconnect/refresh.
    * This restores the answer's existing trail before generation-event replay
    * adds any newer durable ACTIVITY_ITEM events.
   */
  hydrateActivityItems: (messageId, items) => {
    if (!items?.length) return;
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          activityItems: [...items].sort((a, b) => a.seq - b.seq)
        }
      }
    }));
  },
  /**
   * Add an activity item from an ACTIVITY_ITEM SSE event.
   * Deduplicates by seq — a re-emitted item with the same seq replaces the previous one.
   */
  addActivityItem: (messageId, item) => {
    set((state) => {
      const existing = state.activeStreams[messageId]?.activityItems || [];
      const deduped = existing.filter((a) => a.seq !== item.seq);
      return {
        activeStreams: {
          ...state.activeStreams,
          [messageId]: {
            ...state.activeStreams[messageId],
            activityItems: [...deduped, item].sort((a, b) => a.seq - b.seq)
          }
        }
      };
    });
  },
  /**
   * Resolve a tool call entry (from TOOL_END event). Updates hint, status, args, and stdout.
   */
  resolveToolCall: (messageId, { call_id, hint, status, error, args, stdout, stderr, size_hint }) => {
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          toolCalls: (state.activeStreams[messageId]?.toolCalls || []).map(
            (tc) => tc.callId === call_id ? { ...tc, hint, status, error: error || null, args: args !== void 0 ? args || null : tc.args, stdout: stdout || null, stderr: stderr || null, size_hint: size_hint || null } : tc
          )
        }
      }
    }));
  },
  /**
   * Mark stream as completed (successful).
   * Preserves resolved tool calls as a snapshot for optimistic Activity display
   * while database write propagates. Cleared explicitly by clearCompletedSnapshot.
   */
  completeStreaming: (messageId) => {
    set((state) => {
      const stream = state.activeStreams[messageId];
      const completedTools = (stream?.toolCalls || []).filter(
        (tc) => tc.status !== "running"
      );
      const activityItems = stream?.activityItems || [];
      const hasContent = !!stream?.hasContent;
      const newStreams = { ...state.activeStreams };
      if (completedTools.length > 0 || activityItems.length > 0 || hasContent) {
        newStreams[messageId] = {
          streaming: false,
          toolCalls: completedTools,
          activityItems,
          hasContent
        };
      } else {
        delete newStreams[messageId];
      }
      const newPending = { ...state.pendingGenerations };
      delete newPending[messageId];
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      stoppingMessageIds.delete(messageId);
      return { activeStreams: newStreams, pendingGenerations: newPending, stoppingMessageIds };
    });
  },
  /**
   * Clear the completed snapshot once real database activity has arrived.
   */
  clearCompletedSnapshot: (messageId) => {
    set((state) => {
      const stream = state.activeStreams[messageId];
      if (stream && !stream.streaming) {
        const newStreams = { ...state.activeStreams };
        delete newStreams[messageId];
        return { activeStreams: newStreams };
      }
      return state;
    });
  },
  /**
   * Mark stream as failed (error occurred).
   * Preserves any tool calls that completed and activity items that arrived
   * before the error so the Activity panel doesn't go blank mid-session.
   */
  failStreaming: (messageId) => {
    set((state) => {
      const stream = state.activeStreams[messageId];
      const completedTools = (stream?.toolCalls || []).filter(
        (tc) => tc.status !== "running"
      );
      const activityItems = stream?.activityItems || [];
      const newStreams = { ...state.activeStreams };
      if (completedTools.length > 0 || activityItems.length > 0) {
        newStreams[messageId] = { streaming: false, toolCalls: completedTools, activityItems };
      } else {
        delete newStreams[messageId];
      }
      const newPending = { ...state.pendingGenerations };
      delete newPending[messageId];
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      stoppingMessageIds.delete(messageId);
      return { activeStreams: newStreams, pendingGenerations: newPending, stoppingMessageIds };
    });
  },
  /**
   * Link a database generation_id to a streaming message entry.
   * Called when GENERATION_STARTED SSE event arrives. Used by Phase 2 reconnect.
   * Also registers the generation in pendingGenerations so reattach survives navigation.
   */
  setGenerationId: (messageId, generationId) => {
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          generation_id: generationId
        }
      },
      pendingGenerations: {
        ...state.pendingGenerations,
        [messageId]: {
          generation_id: generationId,
          latest_seq: state.pendingGenerations[messageId]?.latest_seq ?? 0
        }
      }
    }));
  },
  /**
   * Update the latest cursor sequence number for a streaming message.
   * Called when a CURSOR event arrives. Persisted in pendingGenerations so
   * reattach can start from the correct position after navigation.
   */
  setLatestSeq: (messageId, seq) => {
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [messageId]: {
          ...state.activeStreams[messageId],
          latest_seq: seq
        }
      },
      pendingGenerations: state.pendingGenerations[messageId] ? {
        ...state.pendingGenerations,
        [messageId]: { ...state.pendingGenerations[messageId], latest_seq: seq }
      } : state.pendingGenerations
    }));
  },
  /**
   * Remove a generation from the pendingGenerations map once it reaches a
   * terminal state. Called by completeStreaming, failStreaming, and the
   * reconnect stream handlers.
   */
  clearPendingGeneration: (messageId) => {
    set((state) => {
      const next = { ...state.pendingGenerations };
      delete next[messageId];
      return { pendingGenerations: next };
    });
  },
  clearMessageState: (messageId) => {
    set((state) => {
      const activeStreams = { ...state.activeStreams };
      const pendingGenerations = { ...state.pendingGenerations };
      const connectionRequirements = { ...state.connectionRequirements };
      const userStoppedIds = new Set(state.userStoppedIds);
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      delete activeStreams[messageId];
      delete pendingGenerations[messageId];
      delete connectionRequirements[messageId];
      userStoppedIds.delete(messageId);
      stoppingMessageIds.delete(messageId);
      return {
        activeStreams,
        pendingGenerations,
        connectionRequirements,
        userStoppedIds,
        stoppingMessageIds
      };
    });
  },
  /**
   * Detach the SSE observer without marking the generation as failed.
   * generation continues and the user can reattach when they return.
   */
  detachStreaming: (messageId) => {
    set((state) => {
      const newStreams = { ...state.activeStreams };
      delete newStreams[messageId];
      return { activeStreams: newStreams };
    });
  },
  /**
   * Check if a message is currently streaming
   */
  isStreaming: (messageId) => {
    return get().activeStreams[messageId]?.streaming || false;
  },
  reconcilePersistedStatus: (messageId, status) => {
    if (!get().isStreaming(messageId)) return;
    if (status === "complete") {
      get().completeStreaming(messageId);
    } else if (["stopped", "failed", "interrupted", "partial", "denied"].includes(status)) {
      get().failStreaming(messageId);
    }
  },
  /**
   * Check if a message has a completed snapshot (stream finished but snapshot not yet cleared).
   * This covers the window between completeStreaming() and clearCompletedSnapshot() where
   * the database write has not yet been consumed by the component — invalidateQueries
   * during this window would cause a duplicate render.
   */
  isCompletedSnapshot: (messageId) => {
    const stream = get().activeStreams[messageId];
    return !!stream && stream.streaming === false;
  },
  /**
   * Check if stream had content (successful stream)
   */
  streamHadContent: (messageId) => {
    return get().activeStreams[messageId]?.hasContent || false;
  },
  /**
   * Mark a message as explicitly stopped by the user.
   * Prevents the database real-time listener from overwriting the partial
   * message with the complete backend-written answer.
   */
  markUserStopped: (messageId) => {
    set((state) => ({
      userStoppedIds: /* @__PURE__ */ new Set([...state.userStoppedIds, messageId])
    }));
  },
  clearUserStopped: (messageId) => {
    set((state) => {
      const userStoppedIds = new Set(state.userStoppedIds);
      userStoppedIds.delete(messageId);
      return { userStoppedIds };
    });
  },
  markStopping: (messageId) => {
    set((state) => ({
      stoppingMessageIds: /* @__PURE__ */ new Set([...state.stoppingMessageIds, messageId])
    }));
  },
  clearStopping: (messageId) => {
    set((state) => {
      const stoppingMessageIds = new Set(state.stoppingMessageIds);
      stoppingMessageIds.delete(messageId);
      return { stoppingMessageIds };
    });
  },
  isStopping: (messageId) => get().stoppingMessageIds.has(messageId),
  /**
   * Check if a message was explicitly stopped by the user
   */
  isUserStopped: (messageId) => {
    return get().userStoppedIds.has(messageId);
  },
  /**
   * Clear all streaming state (useful for cleanup)
   */
  clearAll: () => {
    set({ activeStreams: {}, userStoppedIds: /* @__PURE__ */ new Set(), stoppingMessageIds: /* @__PURE__ */ new Set() });
  }
}));
var streamingStateStore_default = useStreamingStateStore;

// src/cachePersistence.ts
var CHAT_CACHE_STORAGE_KEY = "photon-query-cache";
var CHAT_CACHE_BUSTER = "v1";
var CHAT_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1e3;
var createMemoryPersistenceAdapter = () => {
  const entries = /* @__PURE__ */ new Map();
  return {
    get: async (key) => entries.get(key),
    set: async (key, value) => {
      entries.set(key, value);
    },
    remove: async (key) => {
      entries.delete(key);
    }
  };
};
var createQueryCachePersister = ({
  storage,
  key = CHAT_CACHE_STORAGE_KEY,
  onPersistError
}) => ({
  persistClient: async (client) => {
    try {
      const serialized = JSON.parse(JSON.stringify(client));
      await storage.set(key, serialized);
    } catch (error) {
      if (!onPersistError) throw error;
      onPersistError(error);
    }
  },
  restoreClient: () => storage.get(key),
  removeClient: () => storage.remove(key)
});

// src/activity.ts
var isDisplayableActivity = (item) => item?.type === "intent" || typeof item?.title === "string" && Boolean(item.title.trim()) || typeof item?.description === "string" && Boolean(item.description.trim());
var FILTER = isDisplayableActivity;
var TERMINAL_DELEGATION_STATUSES = /* @__PURE__ */ new Set([
  "completed",
  "responded",
  "failed",
  "interrupted",
  "deferred",
  "unresolved",
  "cancelled",
  "expired"
]);
function orderActivityItems(items = []) {
  return items.map((item, index) => ({ item, index })).sort((left, right) => {
    const leftHasSeq = Number.isFinite(left.item.seq);
    const rightHasSeq = Number.isFinite(right.item.seq);
    if (leftHasSeq && rightHasSeq && left.item.seq !== right.item.seq) {
      return left.item.seq - right.item.seq;
    }
    if (leftHasSeq !== rightHasSeq) return leftHasSeq ? -1 : 1;
    return left.index - right.index;
  }).map(({ item }) => item);
}
function projectDelegationLifecycle(items = []) {
  const orderedItems = orderActivityItems(items);
  const terminalEvents = orderedItems.filter((item) => item.type === "delegation" && item.delegation_id && TERMINAL_DELEGATION_STATUSES.has(item.status || ""));
  const supersedingIndexByDelegation = /* @__PURE__ */ new Map();
  orderedItems.forEach((item, index) => {
    const isTerminalDelegation = item.type === "delegation" && TERMINAL_DELEGATION_STATUSES.has(item.status || "");
    const isApproved = item.type === "approval" && item.status === "approved";
    if (item.delegation_id && (isTerminalDelegation || isApproved)) {
      supersedingIndexByDelegation.set(item.delegation_id, index);
    }
  });
  const terminalByDelegation = new Map(terminalEvents.map((item) => [item.delegation_id, item]));
  const terminalApprovals = new Set(terminalEvents.map((item) => item.approval_id).filter(Boolean));
  return orderedItems.filter((item, index) => !(item.type === "delegation" && item.status === "waiting_approval" && (supersedingIndexByDelegation.get(item.delegation_id || "") ?? -1) > index || item.type === "approval" && ["cancelled", "expired"].includes(item.status || "") && terminalApprovals.has(item.approval_id))).map((item) => {
    if (item.type !== "delegation" || item.status !== "started") return item;
    const terminal = terminalByDelegation.get(item.delegation_id);
    return terminal?.result ? { ...item, result: terminal.result } : item;
  });
}
function getActivityTitle(item) {
  const title = item?.title?.trim() || "Activity";
  if (item?.type === "delegation") {
    const childName = item.child_agent_name || item.agent_name || "Agent";
    const titles = {
      started: `MAS delegated to ${childName}`,
      waiting_approval: `${childName} is waiting for approval`,
      completed: `${childName} completed its assigned task`,
      responded: `${childName} responded`,
      failed: `${childName} failed its assigned task`,
      interrupted: `${childName} was interrupted`,
      deferred: `${childName} deferred while approval is pending`,
      unresolved: `${childName}'s outcome is unresolved`,
      cancelled: `${childName}'s assigned task was cancelled`,
      expired: `${childName}'s approval request expired`
    };
    return titles[item.status || ""] || title;
  }
  const isAgentDelegation = item?.type === "tool" && item.tool_name?.endsWith("_agent");
  return isAgentDelegation ? `${item.status === "running" ? "Delegating to" : "Delegated to"} ${title}` : title;
}
function getDelegationDetails(item) {
  if (item?.type !== "delegation") return [];
  return [
    item.task && { label: "Delegated task", value: item.task },
    item.result && { label: "Result", value: item.result }
  ].filter((entry) => Boolean(entry));
}
function getActivityExecutionId(item) {
  const executionId = item?.agent_id?.startsWith("lepton_") ? item.agent_id : item?.delegation_id;
  return executionId ? executionId.slice(-8) : null;
}
var activityIdentity = (item) => JSON.stringify([
  item.seq,
  item.type,
  item.title,
  item.description,
  item.status,
  item.tool_name,
  item.tool_call_id,
  item.approval_id,
  item.delegation_id,
  item.task,
  item.result,
  item.result_preview,
  item.error_context
]);
function containsActivitySnapshot(persistedItems, streamedItems) {
  if (!persistedItems?.length || !streamedItems?.length) return false;
  const persistedIdentities = new Set(persistedItems.map(activityIdentity));
  return streamedItems.every((item) => persistedIdentities.has(activityIdentity(item)));
}
function selectActivityItems({
  persistedItems,
  streamedItems,
  isActivelyStreaming,
  isCompletedSnapshot
}) {
  const hasPersistedItems = (persistedItems?.length ?? 0) > 0;
  const hasStreamedItems = (streamedItems?.length ?? 0) > 0;
  const persistedCaughtUp = containsActivitySnapshot(persistedItems, streamedItems);
  return isActivelyStreaming || !hasPersistedItems || isCompletedSnapshot && hasStreamedItems && !persistedCaughtUp ? streamedItems : persistedItems;
}
function mergeRunningToolActivity(activityItems = [], toolCalls = []) {
  const completedToolCallIds = new Set(
    activityItems.filter((item) => item.type === "tool").map((item) => item.tool_call_id).filter(Boolean)
  );
  const runningItems = toolCalls.filter((toolCall) => {
    const callId = toolCall.callId || toolCall.call_id;
    return toolCall.status === "running" && !toolCall.name?.startsWith("load_skill") && !completedToolCallIds.has(callId);
  }).map((toolCall) => ({
    type: "tool",
    title: toolCall.displayName || toolCall.display_name || toolCall.name,
    description: "Running\u2026",
    tool_name: toolCall.name,
    tool_call_id: toolCall.callId || toolCall.call_id,
    status: "running",
    args: toolCall.args ?? null,
    code: toolCall.code ?? null,
    agent_id: typeof toolCall.agentId === "string" ? toolCall.agentId : typeof toolCall.agent_id === "string" ? toolCall.agent_id : void 0,
    agent_name: typeof toolCall.agentName === "string" ? toolCall.agentName : typeof toolCall.agent_name === "string" ? toolCall.agent_name : void 0
  }));
  return [...activityItems, ...runningItems];
}
function getItemStyles(item) {
  const status = item?.status;
  if (item?.type === "delegation") {
    if (["failed", "interrupted", "cancelled", "expired"].includes(status || "")) return { dot: "bg-red-400 dark:bg-red-500", title: "text-red-600 dark:text-red-400" };
    if (["waiting_approval", "unresolved", "deferred"].includes(status || "")) return { dot: "bg-amber-400 dark:bg-amber-500", title: "text-amber-700 dark:text-amber-400" };
    if (status === "completed") return { dot: "bg-green-500 dark:bg-green-600", title: "text-green-700 dark:text-green-400" };
    if (status === "responded") return { dot: "bg-fill-activity-neutral dark:bg-fill-activity-neutral-dark", title: "text-ink-gray-600 dark:text-ink-activity-neutral-dark" };
    return { dot: "photon-activity-stage-dot bg-fill-primary dark:bg-fill-activity-active-dark", title: "text-ink-primary dark:text-ink-activity-active-dark" };
  }
  if (item?.type === "tool") {
    if (["error", "failed", "interrupted"].includes(status || "")) return { dot: "bg-red-400 dark:bg-red-500", title: "text-red-600 dark:text-red-400" };
    if (status === "unresolved") return { dot: "bg-amber-400 dark:bg-amber-500", title: "text-amber-700 dark:text-amber-400" };
    if (status === "returned") return { dot: "bg-fill-activity-neutral dark:bg-fill-activity-neutral-dark", title: "text-ink-gray-600 dark:text-ink-activity-neutral-dark" };
    return { dot: "photon-activity-stage-dot bg-fill-primary dark:bg-fill-activity-active-dark", title: "text-ink-primary dark:text-ink-activity-active-dark" };
  }
  if (item?.type === "intent") return { dot: "photon-activity-stage-dot bg-violet-500 dark:bg-violet-400", title: "text-violet-700 dark:text-violet-400" };
  if (item?.type === "plan") return { dot: "photon-activity-stage-dot bg-sky-500 dark:bg-sky-400", title: "text-sky-700 dark:text-sky-400" };
  if (item?.type === "execution_steps") return { dot: "photon-activity-stage-dot bg-teal-500 dark:bg-teal-400", title: "text-teal-700 dark:text-teal-400" };
  if (item?.type === "reasoning_trace") return { dot: "photon-activity-stage-dot bg-amber-400 dark:bg-amber-500", title: "text-amber-700 dark:text-amber-400" };
  if (item?.type === "governance" && status === "passed") return { dot: "bg-green-500 dark:bg-green-600", title: "text-green-700 dark:text-green-400" };
  if (item?.type === "error") return { dot: "bg-red-400 dark:bg-red-500", title: "text-red-600 dark:text-red-400" };
  return { dot: "bg-fill-activity-neutral dark:bg-fill-activity-neutral-dark", title: "text-ink-blue-700 dark:text-ink-blue-400" };
}

// src/cachePolicy.ts
var STALE_NEVER = 0;
var STALE_30S = 30 * 1e3;
var STALE_5MIN = 5 * 60 * 1e3;
var STALE_48H = 48 * 60 * 60 * 1e3;
var GC_7DAYS = 7 * 24 * 60 * 60 * 1e3;
var CACHE_FOREVER = Infinity;
var CHAT_QUERY_KEYS = Object.freeze({
  CHATS: "chats",
  SPECTRAS: "spectras",
  CHAT_MESSAGES: "CHAT_MESSAGES",
  CHAT_MODEL_INFO: "CHAT_MODEL_INFO",
  GET_MODELS: "GET_MODELS",
  GET_MODEL_DEFAULTS: "GET_MODEL_DEFAULTS",
  LIST_SKILLS: "LIST_SKILLS"
});
var chatQueryKeys = Object.freeze({
  personalChats: (orgId, userId) => [CHAT_QUERY_KEYS.CHATS, orgId, userId],
  spectras: (orgId, userId) => [CHAT_QUERY_KEYS.SPECTRAS, orgId, userId],
  spectraSparks: (spaceId, orgId, userId, ownerId) => [
    "spectra-sparks",
    spaceId,
    orgId,
    userId,
    ownerId
  ],
  messages: (chatId, spaceId) => [CHAT_QUERY_KEYS.CHAT_MESSAGES, chatId, spaceId],
  modelInfo: (chatId, spaceId = null) => [
    CHAT_QUERY_KEYS.CHAT_MODEL_INFO,
    chatId,
    spaceId
  ],
  models: (orgId) => [CHAT_QUERY_KEYS.GET_MODELS, orgId],
  modelDefaults: (orgId) => [CHAT_QUERY_KEYS.GET_MODEL_DEFAULTS, orgId],
  skills: (orgId) => [CHAT_QUERY_KEYS.LIST_SKILLS, orgId]
});
var shouldRetryRequest = (failureCount, error, retryLimit) => {
  const status = error?.response?.status ?? error?.status;
  if (status !== void 0 && status >= 400 && status < 500) return false;
  return failureCount < retryLimit;
};
var getRetryDelay = (attemptIndex) => Math.min(1e3 * 2 ** attemptIndex, 3e4);

// src/index.ts
var GENERATION_STATUS = Object.freeze({
  IDLE: "idle",
  GENERATING: "generating",
  RECOVERING: "recovering",
  STOPPING: "stopping",
  STALLED: "stalled",
  AWAITING_APPROVAL: "awaiting_approval",
  STUCK: "stuck",
  COMPLETE: "complete",
  STOPPED: "stopped",
  FAILED: "failed",
  INTERRUPTED: "interrupted",
  PARTIAL: "partial",
  DENIED: "denied"
});
var TERMINAL_ANSWER_STATUSES = /* @__PURE__ */ new Set([
  "complete",
  "stopped",
  "failed",
  "interrupted",
  "partial",
  "denied"
]);
var isTerminalAnswerStatus = (status) => typeof status === "string" && TERMINAL_ANSWER_STATUSES.has(status);
var DISPLAYABLE_EMPTY_ANSWER_STATUSES = /* @__PURE__ */ new Set([
  "stopped",
  "failed",
  "interrupted",
  "partial",
  "denied",
  "expired"
]);
var shouldRenderAnswer = ({
  answerContent,
  answerStatus,
  isError
}) => !!answerContent?.trim() || !!isError || typeof answerStatus === "string" && DISPLAYABLE_EMPTY_ANSWER_STATUSES.has(answerStatus);
var shouldShowMessageActions = ({
  isAnswerDisplayable,
  hasAnswers,
  answerStatus,
  isRegenerating,
  hasSignal,
  isStuck = false
}) => (isAnswerDisplayable || !hasAnswers || isStuck || isTerminalAnswerStatus(answerStatus) || answerStatus === "expired") && !isRegenerating && !hasSignal;
var DISPLAYABLE_TERMINAL_STATUSES = /* @__PURE__ */ new Set([
  "stopped",
  "partial",
  "interrupted",
  "failed",
  "denied",
  "expired"
]);
var shouldShowTerminalStatus = ({
  answer,
  isActivelyStreaming,
  isRegenerating,
  hasSignal
}) => !isActivelyStreaming && !isRegenerating && !hasSignal && !!answer && (typeof answer.status === "string" && DISPLAYABLE_TERMINAL_STATUSES.has(answer.status) || !!answer.isError);
var canRetryAnswer = ({ status, retryable } = {}) => status !== "stopped" && status !== "denied" && retryable !== false;
var shouldClearGenerationSignal = ({
  signalMessageId,
  messageId,
  answerContent,
  answerStatus
}) => signalMessageId === messageId && (!!answerContent?.trim() || isTerminalAnswerStatus(answerStatus));
var isStatusForActiveAnswer = ({
  answerId,
  activeAnswerId
}) => !activeAnswerId || answerId === activeAnswerId;
var RECOVERY_EXCLUDED_STATUSES = /* @__PURE__ */ new Set([
  "stopped",
  "failed",
  "interrupted",
  "partial",
  "complete"
]);
var STUCK_THRESHOLD_SECONDS = 600;
var STATUS_RANK = Object.freeze({
  [GENERATION_STATUS.IDLE]: 0,
  [GENERATION_STATUS.RECOVERING]: 1,
  [GENERATION_STATUS.GENERATING]: 1,
  [GENERATION_STATUS.AWAITING_APPROVAL]: 2,
  [GENERATION_STATUS.STALLED]: 2,
  [GENERATION_STATUS.STUCK]: 2,
  [GENERATION_STATUS.STOPPING]: 2.5,
  [GENERATION_STATUS.COMPLETE]: 3,
  [GENERATION_STATUS.STOPPED]: 3,
  [GENERATION_STATUS.FAILED]: 3,
  [GENERATION_STATUS.INTERRUPTED]: 3,
  [GENERATION_STATUS.PARTIAL]: 3,
  [GENERATION_STATUS.DENIED]: 3
});
function deriveGenerationStatus(input = {}) {
  const {
    hasContent = false,
    isActivelyStreaming = false,
    hasSignal = false,
    isRegenerating = false,
    isAwaitingApproval = false,
    isStopping = false,
    answerStatus,
    elapsedSeconds = 0,
    stuckDismissed = false,
    generateStatus = null,
    isLatest = false,
    recoveryAnswerId,
    recoveryAnswerContent,
    recoveryAnswerStatus,
    recoveryAnswerAwaitingApproval = false
  } = input;
  const isEmptyAwaitingRecovery = isLatest && !hasSignal && !!recoveryAnswerId && !recoveryAnswerContent?.trim() && !recoveryAnswerAwaitingApproval && !(typeof recoveryAnswerStatus === "string" && RECOVERY_EXCLUDED_STATUSES.has(recoveryAnswerStatus));
  const isSkeleton = !hasContent && (isRegenerating || hasSignal || isStopping || isEmptyAwaitingRecovery);
  const isStuck = isEmptyAwaitingRecovery && !hasSignal && !isStopping && elapsedSeconds >= STUCK_THRESHOLD_SECONDS && !stuckDismissed;
  const status = computeStatus({
    hasSignal,
    isActivelyStreaming,
    isRegenerating,
    isAwaitingApproval,
    isStopping,
    answerStatus,
    generateStatus,
    isEmptyAwaitingRecovery,
    isStuck,
    stuckDismissed
  });
  return { status, isSkeleton, isStuck, isEmptyAwaitingRecovery };
}
function computeStatus({
  hasSignal,
  isActivelyStreaming,
  isRegenerating,
  isAwaitingApproval,
  isStopping,
  answerStatus,
  generateStatus,
  isEmptyAwaitingRecovery,
  isStuck,
  stuckDismissed
}) {
  if (!isActivelyStreaming && isTerminalAnswerStatus(answerStatus)) {
    return answerStatus;
  }
  if (isStopping) return GENERATION_STATUS.STOPPING;
  if (isAwaitingApproval) return GENERATION_STATUS.AWAITING_APPROVAL;
  if (isActivelyStreaming || hasSignal || isRegenerating) {
    if (generateStatus === "stalled") return GENERATION_STATUS.STALLED;
    if (generateStatus === "reconnecting") return GENERATION_STATUS.RECOVERING;
    return GENERATION_STATUS.GENERATING;
  }
  if (isEmptyAwaitingRecovery && !stuckDismissed) {
    return isStuck ? GENERATION_STATUS.STUCK : GENERATION_STATUS.RECOVERING;
  }
  return GENERATION_STATUS.IDLE;
}
var LONG_PROMPT_MIN_HEIGHT = 72;
var measurePromptElement = (element) => {
  if (!element?.isConnected) return null;
  return {
    isLong: element.scrollHeight >= LONG_PROMPT_MIN_HEIGHT
  };
};
var shouldCollapseAnswer = ({
  compactMode,
  isLatest,
  ...eligibility
}) => Boolean(
  compactMode && !isLatest && canManuallyCollapseAnswer(eligibility)
);
var canManuallyCollapseAnswer = ({
  isStreaming,
  isRegenerating,
  hasPendingApproval
}) => Boolean(
  !isStreaming && !isRegenerating && !hasPendingApproval
);
var useResolvedMediaUrlStore = create2((set) => ({
  urls: {},
  setLoadedUrl: (filePath, url) => set((state) => ({
    urls: { ...state.urls, [filePath]: url }
  }))
}));
var useChatStore = create2((set) => ({
  activeChat: "",
  editChatId: null,
  editTitle: "",
  addChatDisabled: false,
  messages: [],
  messageRef: [],
  setActiveChat: (chatId) => set({ activeChat: chatId }),
  setEditChatId: (id) => set({ editChatId: id }),
  setEditTitle: (title) => set({ editTitle: title }),
  setAddChatDisabled: (value) => set({ addChatDisabled: value }),
  setMessages: (value) => set({ messages: value }),
  isLatestMessageLoading: false,
  setIsLatestMessageLoading: (value) => set({ isLatestMessageLoading: value }),
  generateChatId: null,
  setGenerateChatId: (value) => set({ generateChatId: value }),
  tempChatId: null,
  setTempChatId: (value) => set({ tempChatId: value })
}));
var getDisplayAnswerId = (answers = []) => {
  const validAnswers = answers.filter((answer) => answer?.id && !answer.awaiting_approval);
  if (validAnswers.length === 1) return validAnswers[0].id ?? null;
  const activeAnswers = validAnswers.filter((answer) => answer.active === true);
  if (activeAnswers.length === 1) return activeAnswers[0].id ?? null;
  return validAnswers[validAnswers.length - 1]?.id ?? null;
};
var resolveSelectedAnswerId = (answers = [], selectedAnswerId) => answers.some((answer) => answer?.id === selectedAnswerId) ? selectedAnswerId : getDisplayAnswerId(answers);
var groupMessagesByTurn = (messages = []) => {
  const grouped = [];
  const indexByTurn = /* @__PURE__ */ new Map();
  messages.forEach((message) => {
    if (message.role && message.role !== "user") {
      grouped.push(message);
      return;
    }
    const turnId = message.turn_id || message.id;
    const answers = (message.answers || []).map((answer) => ({
      ...answer,
      prompt_id: answer.prompt_id || message.id
    }));
    const existingIndex = indexByTurn.get(turnId);
    if (existingIndex === void 0) {
      indexByTurn.set(turnId, grouped.length);
      grouped.push({ ...message, id: turnId, turn_id: turnId, answers });
      return;
    }
    const existing = grouped[existingIndex];
    const existingAnswers = existing.answers || [];
    const existingAnswerIds = new Set(existingAnswers.map((answer) => answer.id));
    grouped[existingIndex] = {
      ...existing,
      answers: [
        ...existingAnswers,
        ...answers.filter((answer) => !existingAnswerIds.has(answer.id))
      ]
    };
  });
  return grouped;
};
var usePromptStore = create2((set) => ({
  prompt: "",
  setPrompt: (newPrompt) => set({ prompt: newPrompt }),
  activeSkills: [],
  setActiveSkills: (skills) => set({ activeSkills: skills }),
  webSearchEnabled: true,
  setWebSearchEnabled: (enabled) => set({ webSearchEnabled: Boolean(enabled) }),
  selectedAnswerByPrompt: {},
  setSelectedAnswer: (promptId, answerId) => set((state) => ({
    selectedAnswerByPrompt: {
      ...state.selectedAnswerByPrompt,
      [promptId]: answerId
    }
  })),
  followLatestAnswer: (promptId, answers) => set((state) => {
    const latestAnswerId = getDisplayAnswerId(answers);
    if (!latestAnswerId || state.selectedAnswerByPrompt[promptId] === latestAnswerId) {
      return state;
    }
    return {
      selectedAnswerByPrompt: {
        ...state.selectedAnswerByPrompt,
        [promptId]: latestAnswerId
      }
    };
  }),
  reconcileSelectedAnswer: (promptId, answers) => set((state) => {
    const currentAnswerId = state.selectedAnswerByPrompt[promptId];
    if (answers?.some((answer) => answer?.id === currentAnswerId)) return state;
    const nextAnswerId = getDisplayAnswerId(answers);
    if (!nextAnswerId) {
      const { [promptId]: _removed, ...remainingSelections } = state.selectedAnswerByPrompt;
      return { selectedAnswerByPrompt: remainingSelections };
    }
    return {
      selectedAnswerByPrompt: {
        ...state.selectedAnswerByPrompt,
        [promptId]: nextAnswerId
      }
    };
  }),
  clearAnswerSelections: () => set({ selectedAnswerByPrompt: {} })
}));
var useGenerationSignalStore = create2((set) => ({
  signals: {},
  addSignal: (chatId, data) => set((state) => ({
    signals: {
      ...state.signals,
      [chatId]: { ...state.signals[chatId] || {}, ...data }
    }
  })),
  removeSignal: (chatId) => set((state) => {
    const signals = { ...state.signals };
    delete signals[chatId];
    return { signals };
  })
}));
var useFeedbackStore = create2((set) => ({
  feedback: null,
  showFeedback: (message, type = "info") => set({ feedback: { message, type } }),
  clearFeedback: () => set({ feedback: null })
}));
var useScrollToBottomStore = create2((set) => ({
  showScrollButton: false,
  setShowScrollButton: (value) => set({ showScrollButton: value })
}));
var isEmptyChatRefetching = ({
  messagesData,
  messagesFetching
}) => messagesFetching && !((messagesData?.messages?.length ?? 0) > 0);
export {
  CACHE_FOREVER,
  CHAT_CACHE_BUSTER,
  CHAT_CACHE_MAX_AGE_MS,
  CHAT_CACHE_STORAGE_KEY,
  CHAT_QUERY_KEYS,
  FILTER,
  GC_7DAYS,
  GENERATION_STATUS,
  LONG_PROMPT_MIN_HEIGHT,
  STALE_30S,
  STALE_48H,
  STALE_5MIN,
  STALE_NEVER,
  STATUS_RANK,
  STUCK_THRESHOLD_SECONDS,
  canManuallyCollapseAnswer,
  canRetryAnswer,
  chatQueryKeys,
  containsActivitySnapshot,
  createMemoryPersistenceAdapter,
  createQueryCachePersister,
  deriveGenerationStatus,
  getActivityExecutionId,
  getActivityTitle,
  getDelegationDetails,
  getDisplayAnswerId,
  getItemStyles,
  getRetryDelay,
  groupMessagesByTurn,
  isDisplayableActivity,
  isEmptyChatRefetching,
  isStatusForActiveAnswer,
  isTerminalAnswerStatus,
  measurePromptElement,
  mergeRunningToolActivity,
  orderActivityItems,
  projectDelegationLifecycle,
  resolveSelectedAnswerId,
  selectActivityItems,
  shouldClearGenerationSignal,
  shouldCollapseAnswer,
  shouldRenderAnswer,
  shouldRetryRequest,
  shouldShowMessageActions,
  shouldShowTerminalStatus,
  useChatStore,
  useFeedbackStore,
  useGenerationSignalStore,
  usePromptStore,
  useResolvedMediaUrlStore,
  useScrollToBottomStore,
  streamingStateStore_default as useStreamingStateStore
};
